import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { Alert, Button } from "react-bootstrap";
import { IoMdClose } from "react-icons/io";
import { supabase } from "./SupabaseClient";
import LoadingModal from "./components/modals/loading-modal";
import { extractGclid } from "./utils/helpers";

const SupabaseAuthContext = createContext();

const updateLoginCookie = (loggedIn) => {
  const hostname = window.location.hostname;

  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";

  const isVercelPreview =
    hostname === "landing-preview.roiquant.com" ||
    hostname === "app-preview.roiquant.com";

  if (isLocalhost) {
    document.cookie = loggedIn
      ? "roiquant_logged_in=true; Path=/; SameSite=Lax"
      : "roiquant_logged_in=; Path=/; Max-Age=0; SameSite=Lax";

    return;
  }

  if (isVercelPreview || hostname.endsWith(".roiquant.com")) {
    document.cookie = loggedIn
      ? "roiquant_logged_in=true; Domain=.roiquant.com; Path=/; Max-Age=31536000; Secure; SameSite=Lax"
      : "roiquant_logged_in=; Domain=.roiquant.com; Path=/; Max-Age=0; Secure; SameSite=Lax";
  }
};

export const useSupabaseApp = () => {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    throw new Error(
      "useSupabaseApp must be used inside <SupabaseAppProvider />",
    );
  }
  return context;
};

export const SupabaseAppProvider = ({ appId, children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  // const app = new Realm.App({ id: appId });

  // Initialize session and subscribe to auth state changes
  useEffect(() => {
    const init = async () => {
      const { data } = await supabase.auth.getSession();
      setCurrentUser(data.session?.user ?? null);
      setSessionLoading(false);

      updateLoginCookie(!!data.session?.user);
    };
    init();

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setCurrentUser(session?.user ?? null);
        setSessionLoading(false);

        updateLoginCookie(!!session?.user);
      },
    );

    return () => subscription.subscription.unsubscribe();
  }, []);

  // -------- Helpers --------
  const getUserTier = useCallback(async () => {
    if (!currentUser) return 1;
    try {
      const session = await supabase.auth.getSession();
      const token = session?.access_token;
      const { data, error } = await supabase.functions.invoke("getUser", {
        body: token,
      });

      if (error) {
        console.error("Error fetching user tier:", error);
        return 1;
      }

      const tier = parseInt(data?.tier ?? "1", 10);
      // console.log("User tier:", tier);
      return tier;
    } catch (err) {
      console.error("Unexpected error fetching user tier:", err);
      return 1;
    }
  }, [currentUser]);

  const getUserDetails = useCallback(async () => {
    if (!currentUser) return null;

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;

      const { data, error } = await supabase.functions.invoke("getUser", {
        body: token,
      });

      if (error) {
        console.error("Error fetching user details:", error);
        return null;
      }

      const user = data?.mongo ?? data;

      return {
        id: data?.id ?? currentUser.id,
        email: data?.email ?? currentUser.email,

        firstname: user?.firstname ?? user?.first_name ?? "",
        lastname: user?.lastname ?? user?.last_name ?? "",

        name:
          user?.name ??
          `${user?.firstname ?? user?.first_name ?? ""} ${
            user?.lastname ?? user?.last_name ?? ""
          }`.trim(),

        tier: parseInt(user?.tier ?? "1", 10),

        signedNdaPD: !!user?.signedNdaPD,

        dob: user?.dob ?? null,
        gender: user?.gender ?? null,
        referredBy: user?.referredBy ?? null,
        adminAccess: !!user?.adminAccess,
        createdAt: user?.createdAt ?? user?.created_at ?? null,
      };
    } catch (err) {
      console.error("Unexpected error fetching user details:", err);
      return null;
    }
  }, [currentUser]);

  const refreshUserDetails = useCallback(async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    setCurrentUser(data.user);
    return getUserDetails();
  }, [getUserDetails]);

  const isLoggedIn = useCallback(() => !!currentUser, [currentUser]);

  // -------- Auth Functions --------
  // Supabase login
  const logIn = useCallback(async (email, password, captchaToken) => {
    const gclid = extractGclid();
    const { data, error } = await supabase.functions.invoke("login", {
      body: { email, password, captchaToken, gclid },
    });

    if (error) {
      const ctx = await error.context?.json?.().catch(() => ({}));
      const msg = ctx?.error ?? "unknown";
      const code = msg.toLowerCase().includes("invalid")
        ? "Invalid username/password"
        : msg.toLowerCase().includes("confirm")
          ? "Confirmation required"
          : msg.toLowerCase().includes("password set")
            ? "No password"
            : "Login error";

      const err = new Error(code + ": " + msg);
      err.errorType = "Login error";

      throw err;
    }

    await supabase.auth.setSession({
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
    });

    setCurrentUser(data.user);
    return data.user;
  }, []);

  const logOut = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
  }, []);

  const register = useCallback(async (email, password, captchaToken) => {
    const gclid = extractGclid();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { captchaToken },
      gclid: gclid,
    });
    if (error) throw error;
    setCurrentUser(data.user);
  }, []);

  const resendConfirmation = useCallback(async (email, captchaToken) => {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { captchaToken },
    });
    if (error) {
      console.log("Resend error:", error.message);
      const err = new Error(error.message);
      err.errorType = "Resend error";

      throw err;
    }
  }, []);

  const confirm = useCallback(() => {
    console.warn("Supabase handles email confirmations automatically.");
  }, []);

  const sendResetPwdEmail = useCallback(async (email, captchaToken) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      captchaToken: captchaToken,
      redirectTo: "https://app.roiquant.com/confirm-password-reset",
    });
    if (error) {
      let errorMessage = "unidentified error";
      if (error.code) errorMessage = error.code;
      else if (error.name) errorMessage = error.name;

      /***
       * Possible error codes:
       * - user_not_found: User to which the API request relates no longer exists.
       ***/
      const err = new Error(errorMessage);
      err.errorType = "Reset password error";

      throw err;
    }
  }, []);

  const completePwdReset = useCallback(async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) {
      let errorCode = "unidentified error";
      if (error.code) errorCode = error.code;
      else if (error.name) errorCode = error.name;

      /***
       * Possible error codes:
       * - same_password: A user that is updating their password must use a different password than the one currently used
       * - session_expired: Session to which the API request relates has expired
       * - session_not_found: Session to which the API request relates no longer exists
       * - weak_password: User is signing up or changing their password without meeting the password strength criteria
       ***/

      throw errorCode;
    }
    setCurrentUser(data.user);
  }, []);

  // -------- Context Value --------
  const value = {
    supabase,
    currentUser,
    logIn,
    logOut,
    register,
    sessionLoading,
    resendConfirmation,
    confirm,
    sendResetPwdEmail,
    completePwdReset,
    getUserTier,
    getUserDetails,
    refreshUserDetails,
    isLoggedIn,
  };

  if (sessionLoading) {
    return (
      <Alert className="error-message floating-alert" variant="light">
        <div>
          <div style={{ textAlign: "right" }}>
            <Button className="pitchdeckmessageclose">
              <IoMdClose />
            </Button>
          </div>
          <LoadingModal />
        </div>
      </Alert>
    );
  }
  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};
